import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AlertTriangle, Cone, TrainFront, Clock, MapPin, ExternalLink, MailWarning, RefreshCw } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LiveCameras from '../components/LiveCameras';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60_000;

const IMPACT_STYLES = {
  HIGH: { border: 'border-l-red-500', text: 'text-red-600', bg: 'bg-red-50', label: 'High impact' },
  MED:  { border: 'border-l-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', label: 'Medium impact' },
  LOW:  { border: 'border-l-slate-300', text: 'text-slate-500', bg: 'bg-slate-50', label: 'Low impact' },
};

const SECTION_META = {
  incidents: { title: 'Live incidents', subtitle: 'Accidents, closures and active disruptions', Icon: AlertTriangle, accent: 'text-red-500' },
  roadworks: { title: 'Planned roadworks', subtitle: 'Next 14 days across Renfrewshire', Icon: Cone, accent: 'text-amber-500' },
  transport: { title: 'Public transport disruptions', subtitle: "ScotRail trains and McGill's buses", Icon: TrainFront, accent: 'text-cyan-600' },
};

const fmtRange = (startsIso, endsIso) => {
  if (!startsIso && !endsIso) return '';
  const fmt = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', {
        weekday: 'short', day: 'numeric', month: 'short',
        hour: '2-digit', minute: '2-digit',
      });
    } catch { return ''; }
  };
  if (startsIso && endsIso) return `${fmt(startsIso)} \u2013 ${fmt(endsIso)}`;
  return fmt(startsIso || endsIso);
};

const relTime = (iso, now) => {
  if (!iso) return '';
  try {
    const diffSec = Math.floor((now - new Date(iso).getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
    return `${Math.floor(diffSec / 86400)} d ago`;
  } catch { return ''; }
};

const Card = ({ item }) => {
  const style = IMPACT_STYLES[item.impact] || IMPACT_STYLES.LOW;
  return (
    <article
      className={`bg-white border border-slate-200 ${style.border} border-l-4 rounded-lg p-4 hover:shadow-md transition-shadow`}
      data-testid={`roadwork-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-semibold text-slate-900 leading-snug">{item.title}</h3>
        <span className={`shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${style.bg} ${style.text}`}>
          {item.impact}
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-2">
        {item.road && <span className="font-semibold text-slate-700">{item.road}</span>}
        {item.area && <span className="inline-flex items-center gap-1"><MapPin size={11} />{item.area}</span>}
        {(item.starts_at || item.ends_at) && (
          <span className="inline-flex items-center gap-1"><Clock size={11} />{fmtRange(item.starts_at, item.ends_at)}</span>
        )}
      </div>
      {item.description && (
        <p className="text-sm text-slate-600 leading-snug line-clamp-3 mb-2">{item.description}</p>
      )}
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-400">Source: {item.source || 'Unknown'}</span>
        {item.source_url && (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-cyan-600 hover:text-cyan-700 font-medium"
            data-testid={`roadwork-source-${item.id}`}
          >
            View source <ExternalLink size={11} />
          </a>
        )}
      </div>
    </article>
  );
};

const Section = ({ kindKey, items }) => {
  const meta = SECTION_META[kindKey];
  const Icon = meta.Icon;
  return (
    <section className="mb-12" data-testid={`section-${kindKey}`}>
      <div className="flex items-center gap-3 mb-1">
        <Icon size={20} className={meta.accent} />
        <h2 className="text-xl font-bold text-slate-900">{meta.title}</h2>
        <span className="text-sm text-slate-400 ml-auto">{items.length}</span>
      </div>
      <p className="text-sm text-slate-500 mb-5">{meta.subtitle}</p>
      {items.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-6 text-center" data-testid={`empty-${kindKey}`}>
          <p className="text-slate-600">No disruptions reported right now {'\u2014'} drive safe.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid={`grid-${kindKey}`}>
          {items.slice(0, 10).map((item) => <Card key={item.id} item={item} />)}
        </div>
      )}
    </section>
  );
};

const RoadsAndTravel = () => {
  const [data, setData] = useState({ incidents: [], roadworks: [], transport: [], updated_at: '' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/roads-and-travel`, { cache: 'no-store' });
      const d = await r.json();
      setData(d);
    } catch (e) {
      // swallow — empty state covers it
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => { setRefreshing(true); load(); setNow(Date.now()); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const updatedAgo = useMemo(() => relTime(data.updated_at, now), [data.updated_at, now]);
  const total = data.incidents.length + data.roadworks.length + data.transport.length;

  return (
    <div className="min-h-screen bg-white" data-testid="roads-and-travel-page">
      <SEO
        title="Roads & Travel in Paisley & Renfrewshire | Paisley.org.uk"
        description="Live roadworks, traffic alerts and transport disruptions across Paisley, Renfrewshire and Glasgow. Updated every 15 minutes."
        keywords="Paisley roadworks, Renfrewshire traffic, M8 closures, ScotRail Paisley disruptions, Paisley roads"
        url="/roads-and-travel"
        image="/images/og/roads.png"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Paisley.org.uk', item: 'https://paisley.org.uk/' },
                { '@type': 'ListItem', position: 2, name: 'Roads & Travel', item: 'https://paisley.org.uk/roads-and-travel' },
              ],
            },
            {
              '@type': 'Dataset',
              name: 'Live Roads & Travel — Paisley & Renfrewshire',
              description: 'Live roadworks, traffic incidents and public transport disruptions for Paisley and Renfrewshire, refreshed every 15 minutes.',
              creator: { '@type': 'Organization', name: 'Paisley.org.uk' },
              url: 'https://paisley.org.uk/roads-and-travel',
              isAccessibleForFree: true,
              license: 'https://paisley.org.uk/terms',
              distribution: [
                { '@type': 'DataDownload', encodingFormat: 'application/json', contentUrl: 'https://paisley.org.uk/api/roads-and-travel' },
              ],
              sourceOrganization: [
                { '@type': 'Organization', name: 'Traffic Scotland', url: 'https://www.traffic.gov.scot' },
                { '@type': 'Organization', name: 'ScotRail', url: 'https://www.scotrail.co.uk' },
              ],
            },
          ],
        }}
      />
      <Header />

      {/* Hero */}
      <section
        className="relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: 'url(/roads-hero.jpg)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/80 to-slate-900/40" />
        <div className="relative max-w-5xl mx-auto px-6 py-20 text-white">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-cyan-300 mb-3 inline-flex items-center gap-2">
            <AlertTriangle size={13} /> Live updates
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3" data-testid="roads-h1">
            Live Roads &amp; Travel
            <span className="block text-2xl md:text-3xl font-light text-slate-200 mt-1">Paisley &amp; Renfrewshire</span>
          </h1>
          <p className="text-sm text-slate-200 max-w-xl mb-5">
            Traffic incidents, roadworks and public transport disruptions across Paisley and Renfrewshire {'\u2014'} refreshed every 15 minutes from Traffic Scotland and ScotRail.
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur ring-1 ring-white/20" data-testid="updated-pill">
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              {data.updated_at ? `Updated ${updatedAgo || 'just now'}` : 'Awaiting first refresh'}
            </span>
            <span className="text-slate-300">{total} active item{total === 1 ? '' : 's'}</span>
          </div>
        </div>
      </section>

      {/* Sections */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        {loading ? (
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-slate-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <Section kindKey="incidents" items={data.incidents || []} />
            <Section kindKey="roadworks" items={data.roadworks || []} />
            <Section kindKey="transport" items={data.transport || []} />
            <LiveCameras />
          </>
        )}
      </main>

      {/* Sticky "Report an issue" button */}
      <a
        href="mailto:traffic@paisley.org.uk?subject=Roads%20%26%20Travel%20%E2%80%94%20Report%20an%20issue"
        className="fixed bottom-6 right-6 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-3 shadow-xl text-sm font-semibold z-30 transition-colors"
        data-testid="report-issue-btn"
      >
        <MailWarning size={16} /> Report an issue
      </a>

      <Footer />
    </div>
  );
};

export default RoadsAndTravel;
